import React, { lazy, Suspense } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import ThreeDotsLoader from '../components/ThreeDotsLoader';
import RouterErrorBoundary from '../components/RouterErrorBoundary';

// Eager load only critical routes
import Home from '../pages/Home';
import Layout from '../components/Layout';

// Lazy load all other routes for code splitting
const Tags = lazy(() => import('../pages/Tags'));
const Bookmarks = lazy(() => import('../pages/Bookmarks'));
const Bookmark = lazy(() => import('../pages/Bookmark'));
const Login = lazy(() => import('../pages/Login'));
const Logout = lazy(() => import('../pages/Logout'));
const Weather = lazy(() => import('../pages/Weather'));
const Dash = lazy(() => import('../pages/Dash'));
const Tasks = lazy(() => import('../pages/Tasks'));
const Task = lazy(() => import('../pages/Task'));
const Projects = lazy(() => import('../pages/Projects'));
const Project = lazy(() => import('../pages/Project'));
const Search = lazy(() => import('../pages/Search'));
const Downloads = lazy(() => import('../pages/Downloads'));
const Clipboard = lazy(() => import('../pages/Clipboard'));
const Users = lazy(() => import('../pages/Users'));
const AiChat = lazy(() => import('../pages/AiChat'));

const Routes = () => {
    const router = createBrowserRouter(
        [
            {
                Component: Layout,
                errorElement: <RouterErrorBoundary />,
                children: [
                    {
                        path: '/dash/*',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={Dash} />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/downloads/*',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={Downloads} />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/weather/*',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={Weather} />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/bookmarks/*',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={Bookmarks} />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/bookmarks/:hash',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={Bookmark} />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/tags/*',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={Tags} />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/tasks/*',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={Tasks} />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/tasks/:id',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={Task} />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/projects/*',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={Projects} />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/projects/:id',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={Project} />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/search/:term',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={Search} />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/search/*',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={Search} />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/clipboard/*',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={Clipboard} />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/users/*',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={Users} />
                            </Suspense>
                        ),
                    },
                    {
                        path: '/ai/*',
                        element: (
                            <Suspense fallback={<ThreeDotsLoader />}>
                                <PrivateRoute component={AiChat} />
                            </Suspense>
                        ),
                    },
                ],
            },
            {
                path: '/',
                element: <Home />,
                errorElement: <RouterErrorBoundary />,
            },
            {
                path: '/login/*',
                element: (
                    <Suspense fallback={<ThreeDotsLoader />}>
                        <Login />
                    </Suspense>
                ),
                errorElement: <RouterErrorBoundary />,
            },
            {
                path: '/logout/*',
                element: (
                    <Suspense fallback={<ThreeDotsLoader />}>
                        <Logout />
                    </Suspense>
                ),
                errorElement: <RouterErrorBoundary />,
            },
        ],
        {
            future: {
                v7_startTransition: true,
                v7_relativeSplatPath: true,
            },
        }
    );

    return <RouterProvider router={router} />;
};

export default Routes;
